import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AinDataColumn, AinDataTableComponent } from '../../shared/design-system/data-table.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { IdentityAdminService, IdentityRole, IdentityUser, PermissionGrantRow } from './identity-admin.service';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';

@Component({
  standalone: true,
  selector: 'ain-users-roles',
  imports: [AinDataTableComponent, AinPageComponent, AinStateComponent, FormsModule, ButtonModule, InputTextModule, TableModule],
  template: `
    <ain-page [eyebrow]="t('identity.eyebrow')" [title]="t('identity.title')" [description]="t('identity.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('identity.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('identity.unavailable')" [message]="error() ?? ''" />
      } @else {
        <div class="grid">
          <ain-data-table title="identity.users" [items]="users()" [columns]="userColumns" />
          <ain-data-table title="identity.roles" [items]="roles()" [columns]="roleColumns" />
        </div>

        <section class="governance">
          <h3>{{ t('identity.governance.title') }}</h3>

          <div class="panel">
            <h4>{{ t('identity.governance.createRole') }}</h4>
            <div class="toolbar">
              <input pInputText [(ngModel)]="newRole.tenantId" [placeholder]="t('columns.tenant')" />
              <input pInputText [(ngModel)]="newRole.name" [placeholder]="t('columns.name')" />
              <input pInputText [(ngModel)]="newRole.systemName" [placeholder]="t('columns.systemName')" />
              <button pButton type="button" [label]="t('common.create')" (click)="createRole()"></button>
            </div>
          </div>

          <div class="panel">
            <h4>{{ t('identity.governance.assignRole') }}</h4>
            <div class="toolbar">
              <select [(ngModel)]="selectedUserId">
                <option value="">{{ t('common.select') }}</option>
                @for (user of users(); track user.id) {
                  <option [value]="user.id">{{ user.displayName }} ({{ user.email }})</option>
                }
              </select>
              <select [(ngModel)]="selectedRoleId">
                <option value="">{{ t('common.select') }}</option>
                @for (role of roles(); track role.id) {
                  <option [value]="role.id">{{ role.name }}</option>
                }
              </select>
              <input pInputText [(ngModel)]="assignmentTenantId" [placeholder]="t('columns.tenant')" />
              <button pButton type="button" [label]="t('identity.governance.assign')" (click)="assignRole()"></button>
              <button pButton type="button" severity="secondary" [label]="t('identity.governance.preview')" (click)="loadEffectiveAccess()"></button>
            </div>
          </div>

          <div class="panel">
            <h4>{{ t('identity.governance.rolePermissions') }}</h4>
            <div class="toolbar">
              <select [(ngModel)]="permissionRoleId">
                <option value="">{{ t('common.select') }}</option>
                @for (role of roles(); track role.id) {
                  <option [value]="role.id">{{ role.name }}</option>
                }
              </select>
              <input pInputText [(ngModel)]="permissionKey" placeholder="workflow.tasks" />
              <input pInputText [(ngModel)]="permissionAction" placeholder="view" />
              <select [(ngModel)]="permissionEffect">
                <option [ngValue]="0">Allow</option>
                <option [ngValue]="1">Deny</option>
              </select>
              <select [(ngModel)]="permissionScopeType">
                <option [ngValue]="0">Global</option>
                <option [ngValue]="1">Tenant</option>
                <option [ngValue]="2">Organization Unit</option>
              </select>
              <button pButton type="button" [label]="t('common.save')" (click)="saveRolePermissions()"></button>
            </div>

            <p-table [value]="rolePermissions()">
              <ng-template pTemplate="header">
                <tr><th>{{ t('columns.name') }}</th><th>{{ t('columns.operation') }}</th><th>{{ t('columns.status') }}</th><th>{{ t('columns.category') }}</th></tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.permissionKey }}</td>
                  <td>{{ row.action }}</td>
                  <td>{{ row.effect === 0 ? 'Allow' : 'Deny' }}</td>
                  <td>{{ row.scopeType }}</td>
                </tr>
              </ng-template>
            </p-table>
          </div>

          <div class="panel">
            <h4>{{ t('identity.governance.effectiveAccess') }}</h4>
            <p-table [value]="effectiveAccess()">
              <ng-template pTemplate="header">
                <tr><th>{{ t('columns.name') }}</th><th>{{ t('columns.operation') }}</th><th>{{ t('columns.status') }}</th><th>{{ t('columns.reference') }}</th></tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.permissionKey }}</td>
                  <td>{{ row.action }}</td>
                  <td>{{ row.effect === 0 ? 'Allow' : 'Deny' }}</td>
                  <td>{{ row.roleId }}</td>
                </tr>
              </ng-template>
            </p-table>
          </div>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; }
    .governance { display: grid; gap: 12px; margin-top: 16px; }
    .panel { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; display: grid; gap: 10px; }
    .panel h4 { margin: 0; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    select { border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px; min-width: 160px; }
  `]
})
export class UsersRolesComponent implements OnInit {
  private readonly identityAdminService = inject(IdentityAdminService);
  private readonly localizationService = inject(LocalizationService);

  readonly users = signal<IdentityUser[]>([]);
  readonly roles = signal<IdentityRole[]>([]);
  readonly rolePermissions = signal<PermissionGrantRow[]>([]);
  readonly effectiveAccess = signal<PermissionGrantRow[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  selectedUserId = '';
  selectedRoleId = '';
  permissionRoleId = '';
  assignmentTenantId = '';
  permissionKey = '';
  permissionAction = 'view';
  permissionEffect = 0;
  permissionScopeType = 0;
  newRole = {
    tenantId: '',
    name: '',
    systemName: ''
  };
  readonly userColumns: AinDataColumn<IdentityUser>[] = [
    { field: 'displayName', header: 'columns.displayName', sortable: true },
    { field: 'email', header: 'columns.email', sortable: true },
    { field: 'tenantId', header: 'columns.tenant' }
  ];
  readonly roleColumns: AinDataColumn<IdentityRole>[] = [
    { field: 'systemName', header: 'columns.systemName', sortable: true },
    { field: 'name', header: 'columns.name', sortable: true },
    { field: 'isSystemRole', header: 'columns.systemRole' }
  ];

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    let completedRequests = 0;
    const markLoaded = (): void => {
      completedRequests += 1;
      if (completedRequests >= 2) {
        this.loading.set(false);
      }
    };

    this.loading.set(true);
    this.error.set(null);
    this.identityAdminService.listUsers({ skip: 0, take: 300 }).subscribe({
      next: users => {
        this.users.set(users.items);
        markLoaded();
      },
      error: () => {
        this.error.set(this.t('identity.errorUsers'));
        this.loading.set(false);
      }
    });

    this.identityAdminService.listRoles({ skip: 0, take: 300 }).subscribe({
      next: roles => {
        this.roles.set(roles.items);
        markLoaded();
      },
      error: () => {
        this.error.set(this.t('identity.errorRoles'));
        this.loading.set(false);
      }
    });
  }

  createRole(): void {
    const payload = {
      tenantId: this.newRole.tenantId.trim(),
      name: this.newRole.name.trim(),
      systemName: this.newRole.systemName.trim(),
      description: '',
      isSystemRole: false
    };

    if (!payload.tenantId || !payload.name || !payload.systemName) {
      return;
    }

    this.identityAdminService.createRole(payload).subscribe({
      next: role => {
        this.roles.update(rows => [role, ...rows]);
        this.newRole.name = '';
        this.newRole.systemName = '';
      }
    });
  }

  assignRole(): void {
    if (!this.selectedUserId || !this.selectedRoleId || !this.assignmentTenantId.trim()) {
      return;
    }

    this.identityAdminService.assignRoleToUser(this.selectedUserId, {
      roleId: this.selectedRoleId,
      tenantId: this.assignmentTenantId.trim(),
      organizationUnitId: null,
      scopeType: 0
    }).subscribe();
  }

  saveRolePermissions(): void {
    if (!this.permissionRoleId || !this.permissionKey.trim() || !this.permissionAction.trim()) {
      return;
    }

    const grant: PermissionGrantRow = {
      roleId: this.permissionRoleId,
      permissionKey: this.permissionKey.trim(),
      action: this.permissionAction.trim(),
      effect: this.permissionEffect,
      scopeType: this.permissionScopeType,
      organizationUnitId: null
    };

    this.identityAdminService.updateRolePermissions(this.permissionRoleId, [grant]).subscribe({
      next: () => this.loadRolePermissions()
    });
  }

  loadRolePermissions(): void {
    if (!this.permissionRoleId) {
      this.rolePermissions.set([]);
      return;
    }

    this.identityAdminService.listRolePermissions(this.permissionRoleId).subscribe({
      next: rows => this.rolePermissions.set(rows)
    });
  }

  loadEffectiveAccess(): void {
    if (!this.selectedUserId) {
      this.effectiveAccess.set([]);
      return;
    }

    this.identityAdminService.getEffectiveAccess(this.selectedUserId).subscribe({
      next: rows => this.effectiveAccess.set(rows)
    });
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }
}
